import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from './ui/button';
import { ShoppingCart, FlaskConical, Sparkles } from 'lucide-react';
import { getLocalizedProductType } from '../utils/productTypeTranslations';

const ProductCard = ({ product }) => {
  const { t, currentLanguage } = useLanguage();
  const { addToCart, loading } = useCart();
  const { formatPrice } = useCurrency();

  const getProductName = () => {
    return product.name[currentLanguage] || product.name.en || product.name;
  };

  const hasVariants = product.variants && product.variants.length > 1;

  const getDisplayPrice = () => {
    if (product.variants && product.variants.length > 0) {
      const prices = product.variants.map(v => v.price);
      return Math.min(...prices);
    }
    return product.price;
  };

  const getPriceLabel = () => {
    if (hasVariants) {
      const fromText = currentLanguage === 'es' ? 'Desde' : 
                       currentLanguage === 'ru' ? 'От' : 'From';
      return `${fromText} ${formatPrice(getDisplayPrice())}`;
    }
    return formatPrice(getDisplayPrice());
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const cartProduct = { ...product };
    if (product.variants && product.variants.length > 0) {
      cartProduct.price = product.variants[0].price;
      cartProduct.selectedDosage = product.variants[0].dosage;
    }
    
    await addToCart(cartProduct, 1);
  };

  return (
    <Link to={`/product/${product.id}`} data-testid={`product-card-${product.id}`}>
      <div className="capsule-product-card group h-full flex flex-col">
        {/* Product Image Container */}
        <div className="relative overflow-hidden rounded-t-[26px] bg-gradient-to-br from-capsule-bg-light via-white to-capsule-bg h-48 md:h-56">
          {/* Decorative Elements */}
          <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-gradient-to-br from-capsule-teal/20 to-capsule-aqua/20 blur-xl"></div>
          <div className="absolute bottom-8 left-4 w-6 h-6 rounded-full bg-gradient-to-br from-capsule-coral/20 to-capsule-salmon/20 blur-lg"></div>
          
          {/* Product Type Badge */}
          {product.product_type && (
            <div className="absolute top-3 left-3 z-10">
              <span className="capsule-badge flex items-center gap-1">
                <FlaskConical className="w-3 h-3" />
                {getLocalizedProductType(product.product_type, currentLanguage)}
              </span>
            </div>
          )}
          
          {/* Product Image */}
          <img
            src={product.image_url || '/placeholder-product.png'}
            alt={getProductName()}
            className="w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-500 ease-out"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/placeholder-product.png';
            }}
          />
          
          {/* Research Disclaimer Overlay */}
          <div className="absolute bottom-3 left-3 right-3">
            <div className="bg-gray-900/80 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-xl text-center">
              {t('research_disclaimer')}
            </div>
          </div>
        </div>

        {/* Product Info */}
        <div className="p-5 flex-1 flex flex-col">
          {/* Product Name */}
          <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 text-sm leading-snug min-h-[2.5rem] group-hover:text-capsule-teal transition-colors">
            {getProductName()}
          </h3>
          
          {/* Dosage - All available dosages as badges (multi-variant), or single dosage if no variants */}
          {hasVariants && product.variants.some(v => v.dosage) ? (
            <div className="mb-2 flex flex-wrap gap-1">
              {product.variants.map((variant, idx) => (
                variant.dosage && (
                  <span key={idx} className="inline-flex items-center gap-1 text-xs bg-capsule-teal/10 text-capsule-teal px-2 py-0.5 rounded-full font-medium">
                    <FlaskConical className="w-3 h-3" />
                    {variant.dosage}
                  </span>
                )
              ))}
            </div>
          ) : product.dosage && (
            <div className="mb-2">
              <span className="inline-flex items-center gap-1 text-xs bg-capsule-teal/10 text-capsule-teal px-2 py-1 rounded-full font-medium">
                <FlaskConical className="w-3 h-3" />
                {product.dosage}
              </span>
            </div>
          )}
          
          {/* Price Section */}
          <div className="mb-4 flex-1">
            <span className="text-xl font-bold bg-gradient-to-r from-capsule-teal to-capsule-aqua bg-clip-text text-transparent" data-testid="product-price">
              {getPriceLabel()}
            </span>
            <span className="block text-xs text-gray-500 mt-1">
              {currentLanguage === 'es' ? 'Impuestos incluidos' : 
               currentLanguage === 'ru' ? 'Включая налоги' : 
               'Tax Included'}
            </span>
          </div>
          
          {/* Add to Cart Button */}
          <Button 
            className="w-full bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white font-semibold py-2.5 rounded-2xl transition-all duration-300 shadow-teal-glow hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-center gap-2"
            onClick={handleAddToCart}
            disabled={loading}
            data-testid="add-to-cart-btn"
          >
            <ShoppingCart className="w-4 h-4" />
            {loading ? '...' : t('add_to_cart')}
          </Button>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
